import { useState } from 'react';
import { formatCurrency } from '@/lib/utils';

const formatDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-CA') : '—';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, RefreshCw, Trash2, Pencil, Eye, Receipt,
  Wallet, CreditCard, TrendingUp, Building2, FileSpreadsheet,
  ChevronDown, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function ChartOfAccounts({ isAdmin, chartOfAccounts, fiscalYear, loadData }) {
  const [showChartAccountModal, setShowChartAccountModal] = useState(false);
  const [editingChartAccount, setEditingChartAccount] = useState(null);
  const [showAccountTransactionsModal, setShowAccountTransactionsModal] = useState(false);
  const [accountTransactions, setAccountTransactions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  const [chartAccountForm, setChartAccountForm] = useState({
    name: '',
    account_type: 'expense',
    account_number: '',
    description: '',
    parent_account_id: '',
    is_active: true
  });

  const resetChartAccountForm = () => {
    setChartAccountForm({
      name: '',
      account_type: 'expense',
      account_number: '',
      description: '',
      parent_account_id: '',
      is_active: true
    });
    setEditingChartAccount(null);
  };

  const handleEditChartAccount = (account) => {
    setEditingChartAccount(account);
    setChartAccountForm({
      name: account.name,
      account_type: account.account_type,
      account_number: account.account_number,
      description: account.description || '',
      parent_account_id: account.parent_account_id || '',
      is_active: account.is_active !== false
    });
    setShowChartAccountModal(true);
  };

  const handleCreateSubAccount = (parentAccount) => {
    resetChartAccountForm();
    const existingSubs = chartOfAccounts.filter(a => a.parent_account_id === parentAccount.id);
    const suffixes = existingSubs.map(a => {
      const parts = a.account_number.split('.');
      return parts.length > 1 ? parseInt(parts[parts.length - 1]) || 0 : 0;
    });
    const nextSuffix = suffixes.length > 0 ? Math.max(...suffixes) + 1 : 1;
    setChartAccountForm({
      name: '',
      account_type: parentAccount.account_type,
      account_number: `${parentAccount.account_number}.${nextSuffix}`,
      description: '',
      parent_account_id: parentAccount.id,
      is_active: true
    });
    setShowChartAccountModal(true);
  };

  const saveChartAccount = async () => {
    if (!chartAccountForm.name || !chartAccountForm.account_number) {
      toast.error('Account name and number are required');
      return;
    }
    const token = getToken();
    try {
      if (editingChartAccount) {
        await axios.put(
          `${API}/api/accounting/chart-of-accounts/${editingChartAccount.id}`,
          chartAccountForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Account updated successfully');
      } else {
        await axios.post(
          `${API}/api/accounting/chart-of-accounts`,
          chartAccountForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Account created successfully');
      }
      setShowChartAccountModal(false);
      resetChartAccountForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save account');
    }
  };

  const deleteChartAccount = async (account) => {
    if (!confirm(`Are you sure you want to delete "${account.name}" (${account.account_number})? This action cannot be undone.`)) return;
    const token = getToken();
    try {
      await axios.delete(
        `${API}/api/accounting/chart-of-accounts/${account.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Account "${account.name}" deleted`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
    }
  };

  const handleAccountClick = async (account) => {
    setSelectedAccount(account);
    setDetailsLoading(true);
    const token = getToken();
    try {
      const response = await axios.get(
        `${API}/api/accounting/chart-of-accounts/${account.id}/details?fiscal_year=${fiscalYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAccountTransactions(response.data);
      setShowAccountTransactionsModal(true);
    } catch (err) {
      toast.error('Failed to load account details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const recalculateBalances = async () => {
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/chart-of-accounts/recalculate-balances`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadData();
    } catch (err) {
      toast.error('Failed to recalculate balances');
    }
  };

  const getHierarchicalAccounts = (accounts) => {
    const parents = accounts.filter(a => !a.parent_account_id);
    const children = accounts.filter(a => a.parent_account_id);
    const result = [];
    parents.forEach(parent => {
      result.push(parent);
      children.filter(c => c.parent_account_id === parent.id).forEach(child => result.push(child));
    });
    const addedIds = new Set(result.map(a => a.id));
    children.filter(c => !addedIds.has(c.id)).forEach(c => result.push(c));
    return result;
  };

  return (
    <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Chart of Accounts</CardTitle>
                <CardDescription>Manage your accounting categories. Click an account to view transactions.</CardDescription>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={recalculateBalances} data-testid="recalculate-balances-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Recalculate Balances
                  </Button>
                  <Button onClick={() => { resetChartAccountForm(); setShowChartAccountModal(true); }} data-testid="create-account-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Account
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {/* Grouped Chart of Accounts */}
              <div className="space-y-6 overflow-x-auto">
                {/* Assets Section */}
                {chartOfAccounts.filter(a => a.account_type === 'asset').length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                          <Wallet className="w-5 h-5" />
                          Assets
                        </h3>
                        <span className="font-mono text-lg text-blue-700">
                          {formatCurrency(chartOfAccounts.filter(a => a.account_type === 'asset').reduce((sum, a) => sum + (a.balance || 0), 0))}
                        </span>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">Resources owned by the business</p>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-blue-50/50">
                          <TableHead className="w-[120px]">Account #</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead className="text-center w-[80px]">Status</TableHead>
                          {isAdmin && <TableHead className="text-right w-[120px]">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getHierarchicalAccounts(chartOfAccounts.filter(a => a.account_type === 'asset')).map(account => (
                          <TableRow 
                            key={account.id} 
                            className={`cursor-pointer hover:bg-blue-50 ${account.parent_account_id ? 'bg-slate-50' : ''}`}
                            onClick={() => handleAccountClick(account)}
                            data-testid={`account-row-${account.account_number}`}
                          >
                            <TableCell className="font-mono">
                              {account.parent_account_id && <span className="text-slate-400 mr-2">↳</span>}
                              {account.account_number}
                            </TableCell>
                            <TableCell className={account.parent_account_id ? 'pl-6 font-medium' : 'font-medium'}>{account.name}</TableCell>
                            <TableCell className="max-w-[200px] truncate text-slate-500 text-sm">{account.description || '—'}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(account.balance)}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant={account.is_active !== false ? 'default' : 'secondary'} className={account.is_active !== false ? 'bg-emerald-100 text-emerald-700' : ''}>
                                {account.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleAccountClick(account); }} title="View"><Eye className="w-4 h-4 text-blue-500" /></Button>
                                  {!account.parent_account_id && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCreateSubAccount(account); }} title="Add Sub-Account" data-testid={`add-sub-account-${account.account_number}`}><Plus className="w-4 h-4 text-emerald-500" /></Button>}
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditChartAccount(account); }} title="Edit"><Pencil className="w-4 h-4 text-slate-500" /></Button>
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteChartAccount(account); }} title="Delete"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Liabilities & Credit Cards Section */}
                {chartOfAccounts.filter(a => a.account_type === 'liability').length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-red-50 px-4 py-3 border-b border-red-200">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-red-800 flex items-center gap-2">
                          <CreditCard className="w-5 h-5" />
                          Liabilities & Credit Cards
                        </h3>
                        <span className="font-mono text-lg text-red-700">
                          {formatCurrency(chartOfAccounts.filter(a => a.account_type === 'liability').reduce((sum, a) => sum + (a.balance || 0), 0))}
                        </span>
                      </div>
                      <p className="text-xs text-red-600 mt-1">Debts and obligations owed by the business</p>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-red-50/50">
                          <TableHead className="w-[120px]">Account #</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead className="text-center w-[80px]">Status</TableHead>
                          {isAdmin && <TableHead className="text-right w-[120px]">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getHierarchicalAccounts(chartOfAccounts.filter(a => a.account_type === 'liability')).map(account => (
                          <TableRow 
                            key={account.id} 
                            className={`cursor-pointer hover:bg-red-50 ${account.parent_account_id ? 'bg-slate-50' : ''}`}
                            onClick={() => handleAccountClick(account)}
                            data-testid={`account-row-${account.account_number}`}
                          >
                            <TableCell className="font-mono">
                              {account.parent_account_id && <span className="text-slate-400 mr-2">↳</span>}
                              {account.account_number}
                            </TableCell>
                            <TableCell className={account.parent_account_id ? 'pl-6 font-medium' : 'font-medium'}>{account.name}</TableCell>
                            <TableCell className="max-w-[200px] truncate text-slate-500 text-sm">{account.description || '—'}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(account.balance)}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant={account.is_active !== false ? 'default' : 'secondary'} className={account.is_active !== false ? 'bg-emerald-100 text-emerald-700' : ''}>
                                {account.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleAccountClick(account); }} title="View"><Eye className="w-4 h-4 text-blue-500" /></Button>
                                  {!account.parent_account_id && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCreateSubAccount(account); }} title="Add Sub-Account" data-testid={`add-sub-account-${account.account_number}`}><Plus className="w-4 h-4 text-emerald-500" /></Button>}
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditChartAccount(account); }} title="Edit"><Pencil className="w-4 h-4 text-slate-500" /></Button>
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteChartAccount(account); }} title="Delete"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Income Section */}
                {chartOfAccounts.filter(a => a.account_type === 'income').length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-200">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-emerald-800 flex items-center gap-2">
                          <TrendingUp className="w-5 h-5" />
                          Income
                        </h3>
                        <span className="font-mono text-lg text-emerald-700">
                          {formatCurrency(chartOfAccounts.filter(a => a.account_type === 'income').reduce((sum, a) => sum + (a.balance || 0), 0))}
                        </span>
                      </div>
                      <p className="text-xs text-emerald-600 mt-1">Revenue earned from business operations</p>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-emerald-50/50">
                          <TableHead className="w-[120px]">Account #</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead className="text-center w-[80px]">Status</TableHead>
                          {isAdmin && <TableHead className="text-right w-[120px]">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getHierarchicalAccounts(chartOfAccounts.filter(a => a.account_type === 'income')).map(account => (
                          <TableRow 
                            key={account.id} 
                            className={`cursor-pointer hover:bg-emerald-50 ${account.parent_account_id ? 'bg-slate-50' : ''}`}
                            onClick={() => handleAccountClick(account)}
                            data-testid={`account-row-${account.account_number}`}
                          >
                            <TableCell className="font-mono">
                              {account.parent_account_id && <span className="text-slate-400 mr-2">↳</span>}
                              {account.account_number}
                            </TableCell>
                            <TableCell className={account.parent_account_id ? 'pl-6 font-medium' : 'font-medium'}>{account.name}</TableCell>
                            <TableCell className="max-w-[200px] truncate text-slate-500 text-sm">{account.description || '—'}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-700">{formatCurrency(account.balance)}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant={account.is_active !== false ? 'default' : 'secondary'} className={account.is_active !== false ? 'bg-emerald-100 text-emerald-700' : ''}>
                                {account.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleAccountClick(account); }} title="View"><Eye className="w-4 h-4 text-blue-500" /></Button>
                                  {!account.parent_account_id && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCreateSubAccount(account); }} title="Add Sub-Account" data-testid={`add-sub-account-${account.account_number}`}><Plus className="w-4 h-4 text-emerald-500" /></Button>}
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditChartAccount(account); }} title="Edit"><Pencil className="w-4 h-4 text-slate-500" /></Button>
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteChartAccount(account); }} title="Delete"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Expenses Section */}
                {chartOfAccounts.filter(a => a.account_type === 'expense').length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                          <Receipt className="w-5 h-5" />
                          Expenses
                        </h3>
                        <span className="font-mono text-lg text-amber-700">
                          {formatCurrency(chartOfAccounts.filter(a => a.account_type === 'expense').reduce((sum, a) => sum + (a.balance || 0), 0))}
                        </span>
                      </div>
                      <p className="text-xs text-amber-600 mt-1">Costs incurred in business operations</p>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-50/50">
                          <TableHead className="w-[120px]">Account #</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead className="text-center w-[80px]">Status</TableHead>
                          {isAdmin && <TableHead className="text-right w-[120px]">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getHierarchicalAccounts(chartOfAccounts.filter(a => a.account_type === 'expense')).map(account => (
                          <TableRow 
                            key={account.id} 
                            className={`cursor-pointer hover:bg-amber-50 ${account.parent_account_id ? 'bg-slate-50' : ''}`}
                            onClick={() => handleAccountClick(account)}
                            data-testid={`account-row-${account.account_number}`}
                          >
                            <TableCell className="font-mono">
                              {account.parent_account_id && <span className="text-slate-400 mr-2">↳</span>}
                              {account.account_number}
                            </TableCell>
                            <TableCell className={account.parent_account_id ? 'pl-6 font-medium' : 'font-medium'}>{account.name}</TableCell>
                            <TableCell className="max-w-[200px] truncate text-slate-500 text-sm">{account.description || '—'}</TableCell>
                            <TableCell className="text-right font-mono text-amber-700">{formatCurrency(account.balance)}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant={account.is_active !== false ? 'default' : 'secondary'} className={account.is_active !== false ? 'bg-emerald-100 text-emerald-700' : ''}>
                                {account.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleAccountClick(account); }} title="View"><Eye className="w-4 h-4 text-blue-500" /></Button>
                                  {!account.parent_account_id && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCreateSubAccount(account); }} title="Add Sub-Account" data-testid={`add-sub-account-${account.account_number}`}><Plus className="w-4 h-4 text-emerald-500" /></Button>}
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditChartAccount(account); }} title="Edit"><Pencil className="w-4 h-4 text-slate-500" /></Button>
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteChartAccount(account); }} title="Delete"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Equity Section */}
                {chartOfAccounts.filter(a => a.account_type === 'equity').length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-purple-50 px-4 py-3 border-b border-purple-200">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                          <Building2 className="w-5 h-5" />
                          Equity
                        </h3>
                        <span className="font-mono text-lg text-purple-700">
                          {formatCurrency(chartOfAccounts.filter(a => a.account_type === 'equity').reduce((sum, a) => sum + (a.balance || 0), 0))}
                        </span>
                      </div>
                      <p className="text-xs text-purple-600 mt-1">Owner's stake and retained earnings</p>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-purple-50/50">
                          <TableHead className="w-[120px]">Account #</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead className="text-center w-[80px]">Status</TableHead>
                          {isAdmin && <TableHead className="text-right w-[120px]">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getHierarchicalAccounts(chartOfAccounts.filter(a => a.account_type === 'equity')).map(account => (
                          <TableRow 
                            key={account.id} 
                            className={`cursor-pointer hover:bg-purple-50 ${account.parent_account_id ? 'bg-slate-50' : ''}`}
                            onClick={() => handleAccountClick(account)}
                            data-testid={`account-row-${account.account_number}`}
                          >
                            <TableCell className="font-mono">
                              {account.parent_account_id && <span className="text-slate-400 mr-2">↳</span>}
                              {account.account_number}
                            </TableCell>
                            <TableCell className={account.parent_account_id ? 'pl-6 font-medium' : 'font-medium'}>{account.name}</TableCell>
                            <TableCell className="max-w-[200px] truncate text-slate-500 text-sm">{account.description || '—'}</TableCell>
                            <TableCell className="text-right font-mono text-purple-700">{formatCurrency(account.balance)}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant={account.is_active !== false ? 'default' : 'secondary'} className={account.is_active !== false ? 'bg-emerald-100 text-emerald-700' : ''}>
                                {account.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleAccountClick(account); }} title="View"><Eye className="w-4 h-4 text-blue-500" /></Button>
                                  {!account.parent_account_id && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCreateSubAccount(account); }} title="Add Sub-Account" data-testid={`add-sub-account-${account.account_number}`}><Plus className="w-4 h-4 text-emerald-500" /></Button>}
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditChartAccount(account); }} title="Edit"><Pencil className="w-4 h-4 text-slate-500" /></Button>
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteChartAccount(account); }} title="Delete"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Summary Totals */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-slate-100 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-blue-600 font-medium">Assets</p>
                    <p className="font-mono font-bold text-blue-700">{formatCurrency(chartOfAccounts.filter(a => a.account_type === 'asset').reduce((sum, a) => sum + (a.balance || 0), 0))}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-red-600 font-medium">Liabilities</p>
                    <p className="font-mono font-bold text-red-700">{formatCurrency(chartOfAccounts.filter(a => a.account_type === 'liability').reduce((sum, a) => sum + (a.balance || 0), 0))}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-emerald-600 font-medium">Income</p>
                    <p className="font-mono font-bold text-emerald-700">{formatCurrency(chartOfAccounts.filter(a => a.account_type === 'income').reduce((sum, a) => sum + (a.balance || 0), 0))}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-amber-600 font-medium">Expenses</p>
                    <p className="font-mono font-bold text-amber-700">{formatCurrency(chartOfAccounts.filter(a => a.account_type === 'expense').reduce((sum, a) => sum + (a.balance || 0), 0))}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-purple-600 font-medium">Equity</p>
                    <p className="font-mono font-bold text-purple-700">{formatCurrency(chartOfAccounts.filter(a => a.account_type === 'equity').reduce((sum, a) => sum + (a.balance || 0), 0))}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        

      <Dialog open={showChartAccountModal} onOpenChange={(open) => {
        setShowChartAccountModal(open);
        if (!open) { resetChartAccountForm(); }
      }}>
        <DialogContent data-testid="chart-account-modal">
          <DialogHeader>
            <DialogTitle>
              {editingChartAccount ? 'Edit Account' : chartAccountForm.parent_account_id ? 'Create Sub-Account' : 'Create Account'}
            </DialogTitle>
            <DialogDescription>
              {chartAccountForm.parent_account_id 
                ? `Creating sub-account under ${chartOfAccounts.find(a => a.id === chartAccountForm.parent_account_id)?.name || 'parent'}`
                : editingChartAccount 
                  ? 'Update account details below'
                  : 'Add a new account to your chart of accounts'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Number *</Label>
                <Input
                  value={chartAccountForm.account_number}
                  onChange={(e) => setChartAccountForm({ ...chartAccountForm, account_number: e.target.value })}
                  placeholder="e.g., 5100 or 5100.1"
                  data-testid="account-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Account Type *</Label>
                <Select
                  value={chartAccountForm.account_type}
                  onValueChange={(v) => setChartAccountForm({ ...chartAccountForm, account_type: v })}
                >
                  <SelectTrigger data-testid="account-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                    <SelectItem value="equity">Equity</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Account Name *</Label>
              <Input
                value={chartAccountForm.name}
                onChange={(e) => setChartAccountForm({ ...chartAccountForm, name: e.target.value })}
                placeholder="e.g., Advertising & Marketing"
                data-testid="account-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={chartAccountForm.description}
                onChange={(e) => setChartAccountForm({ ...chartAccountForm, description: e.target.value })}
                placeholder="Optional description for this account"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Parent Account (for sub-accounts)</Label>
              <Select
                value={chartAccountForm.parent_account_id || "none"}
                onValueChange={(v) => setChartAccountForm({ ...chartAccountForm, parent_account_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="parent-account-select">
                  <SelectValue placeholder="No parent (top-level account)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent (top-level account)</SelectItem>
                  {chartOfAccounts.filter(a => !a.parent_account_id).map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.account_number} - {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={chartAccountForm.is_active}
                onChange={(e) => setChartAccountForm({ ...chartAccountForm, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Account is Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChartAccountModal(false)}>Cancel</Button>
            <Button onClick={saveChartAccount} data-testid="save-account-btn">
              {editingChartAccount ? 'Update Account' : 'Create Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}

      <Dialog open={showAccountTransactionsModal} onOpenChange={setShowAccountTransactionsModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="account-transactions-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              {selectedAccount?.account_number} - {selectedAccount?.name}
            </DialogTitle>
            <DialogDescription>
              All transactions for this account in {fiscalYear}
            </DialogDescription>
          </DialogHeader>

          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Account Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-xs text-blue-600 uppercase">Total Transactions</p>
                  <p className="text-xl font-bold text-blue-700">{accountTransactions.length}</p>
                </div>
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                  <p className="text-xs text-emerald-600 uppercase">Total Debits</p>
                  <p className="text-xl font-bold font-mono text-emerald-700">
                    {formatCurrency(accountTransactions.reduce((sum, t) => sum + (t.debit || 0), 0))}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-xs text-red-600 uppercase">Total Credits</p>
                  <p className="text-xl font-bold font-mono text-red-700">
                    {formatCurrency(accountTransactions.reduce((sum, t) => sum + (t.credit || 0), 0))}
                  </p>
                </div>
              </div>

              {/* Transactions Table */}
              {accountTransactions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No transactions found for this account in {fiscalYear}
                </div>
              ) : (
                <div className="border rounded-lg overflow-auto" style={{ maxHeight: '400px' }}>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr>
                        <th className="text-left p-2 font-medium">Date</th>
                        <th className="text-left p-2 font-medium">Description</th>
                        <th className="text-left p-2 font-medium">Category</th>
                        <th className="text-left p-2 font-medium">Vendor</th>
                        <th className="text-left p-2 font-medium">Reference</th>
                        <th className="text-right p-2 font-medium">Debit</th>
                        <th className="text-right p-2 font-medium">Credit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {accountTransactions.map((trans, idx) => (
                        <tr 
                          key={idx} 
                          className="hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => {
                            setShowAccountTransactionsModal(false);
                            // Navigate based on source
                            if (trans.source === 'expense') {
                              window.location.href = '/expenses';
                            } else if (trans.source === 'commission') {
                              window.location.href = '/commissions';
                            } else {
                              // Stay on accounting transactions tab
                              setActiveTab('transactions');
                            }
                          }}
                        >
                          <td className="p-2">{formatDate(trans.date)}</td>
                          <td className="p-2 max-w-xs truncate" title={trans.description}>{trans.description}</td>
                          <td className="p-2">{trans.category || '—'}</td>
                          <td className="p-2">{trans.vendor || '—'}</td>
                          <td className="p-2 font-mono text-xs">{trans.reference || '—'}</td>
                          <td className="p-2 text-right font-mono text-emerald-600">
                            {trans.debit > 0 ? formatCurrency(trans.debit) : '—'}
                          </td>
                          <td className="p-2 text-right font-mono text-red-600">
                            {trans.credit > 0 ? formatCurrency(trans.credit) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-100 sticky bottom-0">
                      <tr>
                        <td colSpan={5} className="p-2 font-semibold text-right">Net Balance:</td>
                        <td colSpan={2} className="p-2 text-right font-mono font-bold">
                          {formatCurrency(selectedAccount?.balance || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccountTransactionsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Preview Modal */}


    </>
  );
}
